import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SendIcon, MessageCircleIcon, ChevronDownIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { saveSubmission } from './AdminDashboard';
import type { NetworkSubmission } from './AdminDashboard';
interface JoinNetworkFormProps {
  isOpen: boolean;
  onClose: () => void;
}
// Reason keys used internally (language-independent)
type ReasonKey = 'keepInformed' | 'volunteer' | 'collegeStudent' | 'socialMedia' | 'nonprofit' | 'faithBased' | 'business' | 'accomplishments';
interface ReasonOption {
  key: ReasonKey;
  labelEn: string;
  labelEs: string;
}
const REASON_OPTIONS: ReasonOption[] = [{
  key: 'keepInformed',
  labelEn: 'Keep me informed',
  labelEs: 'Manténganme informado'
}, {
  key: 'volunteer',
  labelEn: 'Interested in volunteering with the organization',
  labelEs: 'Interesado en ser voluntario con la organización'
}, {
  key: 'collegeStudent',
  labelEn: 'I am a college student looking to connect with fellow Honduran-American peers',
  labelEs: 'Soy estudiante universitario y busco conectar con otros jóvenes hondureño-americanos'
}, {
  key: 'socialMedia',
  labelEn: 'Share my social media platforms to highlight',
  labelEs: 'Compartir mis plataformas de redes sociales para destacar'
}, {
  key: 'nonprofit',
  labelEn: 'Share your nonprofit supporting Honduran-Americans/Honduras',
  labelEs: 'Comparte tu organización sin fines de lucro que apoya a hondureño-americanos/Honduras'
}, {
  key: 'faithBased',
  labelEn: 'Share the faith-based organization you lead',
  labelEs: 'Comparte la organización religiosa que lideras'
}, {
  key: 'business',
  labelEn: 'Share your Honduran-American business',
  labelEs: 'Comparte tu negocio hondureño-americano'
}, {
  key: 'accomplishments',
  labelEn: 'Share your accomplishments & story',
  labelEs: 'Comparte tus logros e historia'
}];
const US_STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC', 'PR', 'Other'];
const WHATSAPP_NUMBER = '12029001234';
const formatPhoneNumber = (value: string): string => {
  // Strip all non-digits
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};
const inputClass = 'w-full px-4 py-3.5 rounded-xl border-2 border-gray-200 font-nunito text-sm text-haa-navy focus:border-haa-blue focus:ring-0 outline-none transition-colors';
const subInputClass = 'w-full px-4 py-3 rounded-lg border border-gray-200 font-nunito text-sm text-haa-navy focus:border-haa-blue focus:ring-0 outline-none transition-colors bg-white';
export function JoinNetworkForm({
  isOpen,
  onClose
}: JoinNetworkFormProps) {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [selectedReasons, setSelectedReasons] = useState<ReasonKey[]>([]);
  const [identifiesHonduran, setIdentifiesHonduran] = useState<'yes' | 'no' | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  // Sub-fields for social media
  const [socialInstagram, setSocialInstagram] = useState('');
  const [socialFacebook, setSocialFacebook] = useState('');
  const [socialTiktok, setSocialTiktok] = useState('');
  // Sub-fields for nonprofit
  const [nonprofitName, setNonprofitName] = useState('');
  const [nonprofitDirector, setNonprofitDirector] = useState('');
  const [nonprofitWebsite, setNonprofitWebsite] = useState('');
  // Sub-fields for faith-based organization
  const [faithCity, setFaithCity] = useState('');
  const [faithState, setFaithState] = useState('');
  const [faithOrgName, setFaithOrgName] = useState('');
  const [faithWebsite, setFaithWebsite] = useState('');
  // Sub-fields for business
  const [businessName, setBusinessName] = useState('');
  const [businessContact, setBusinessContact] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessWebsite, setBusinessWebsite] = useState('');
  // Sub-fields for accomplishments
  const [accomplishmentWebsite, setAccomplishmentWebsite] = useState('');
  const [accomplishmentSocial, setAccomplishmentSocial] = useState('');
  const [accomplishmentLinkedin, setAccomplishmentLinkedin] = useState('');
  const l = isEs ? {
    title: 'Únete a Nuestra Red',
    subtitle: 'Completa el formulario y conecta con la Asociación Hondureño-Americana.',
    firstName: 'Nombre',
    lastName: 'Apellido o Inicial',
    email: 'Correo Electrónico',
    phone: 'Teléfono (opcional)',
    city: 'Ciudad',
    state: 'Estado',
    selectState: 'Seleccionar estado',
    reasonLabel: 'Razón de Contacto',
    required: 'Los campos marcados con * son obligatorios',
    successTitle: '¡Enviado!',
    successMessage: 'Tu información ha sido enviada. Nos pondremos en contacto pronto.',
    close: 'Cerrar',
    or: 'o',
    // Social media sub-field labels
    instagram: 'Instagram',
    facebook: 'Facebook',
    tiktok: 'TikTok',
    // Sub-field labels
    nonprofitName: 'Nombre de la Organización',
    nonprofitDirector: 'Director(a)',
    nonprofitWebsite: 'Sitio Web',
    faithCity: 'Ciudad',
    faithState: 'Estado',
    faithOrgName: 'Nombre de la Organización',
    faithWebsite: 'Sitio Web o Red Social',
    businessName: 'Nombre del Negocio',
    businessContact: 'Persona de Contacto',
    businessPhone: 'Teléfono del Negocio',
    businessWebsite: 'Sitio Web o Red Social',
    accWebsite: 'Sitio Web',
    accSocial: 'Red Social',
    accLinkedin: 'LinkedIn'
  } : {
    title: 'Join Our Network',
    subtitle: 'Fill out the form below to connect with the Honduran-American Association.',
    firstName: 'First Name',
    lastName: 'Last Name or Initial',
    email: 'Email',
    phone: 'Phone (optional)',
    city: 'City',
    state: 'State',
    selectState: 'Select state',
    reasonLabel: 'Reason for Contacting',
    required: 'Fields marked with * are required',
    successTitle: 'Sent!',
    successMessage: "Your information has been submitted. We'll be in touch soon.",
    close: 'Close',
    or: 'or',
    // Social media sub-field labels
    instagram: 'Instagram',
    facebook: 'Facebook',
    tiktok: 'TikTok',
    // Sub-field labels
    nonprofitName: 'Nonprofit Name',
    nonprofitDirector: 'Director',
    nonprofitWebsite: 'Website',
    faithCity: 'City',
    faithState: 'State',
    faithOrgName: 'Organization Name',
    faithWebsite: 'Website or Social Media Link',
    businessName: 'Business Name',
    businessContact: 'Contact Person',
    businessPhone: 'Business Phone',
    businessWebsite: 'Website or Social Media Handle',
    accWebsite: 'Website',
    accSocial: 'Social Media',
    accLinkedin: 'LinkedIn'
  };
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const toggleReason = (key: ReasonKey) => {
    setSelectedReasons(prev => prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]);
  };
  const getReasonLabel = (opt: ReasonOption) => isEs ? opt.labelEs : opt.labelEn;
  const buildMessageBody = () => {
    const reasonLabels = selectedReasons.map(key => {
      const opt = REASON_OPTIONS.find(o => o.key === key);
      return opt ? getReasonLabel(opt) : key;
    });
    const reasonsText = reasonLabels.length > 0 ? reasonLabels.join('; ') : 'N/A';
    let body = `${l.firstName}: ${firstName}
${l.lastName}: ${lastName}
${l.email}: ${email}
${l.phone}: ${phone}
${l.city}: ${city}
${l.state}: ${state}
${l.reasonLabel}: ${reasonsText}`;
    if (selectedReasons.includes('socialMedia') && (socialInstagram || socialFacebook || socialTiktok)) {
      body += `\n\n--- ${isEs ? 'Redes Sociales' : 'Social Media Platforms'} ---`;
      if (socialInstagram) body += `\n${l.instagram}: ${socialInstagram}`;
      if (socialFacebook) body += `\n${l.facebook}: ${socialFacebook}`;
      if (socialTiktok) body += `\n${l.tiktok}: ${socialTiktok}`;
    }
    if (selectedReasons.includes('nonprofit') && (nonprofitName || nonprofitDirector || nonprofitWebsite)) {
      body += `\n\n--- ${isEs ? 'Información de la Organización' : 'Nonprofit Details'} ---`;
      if (nonprofitName) body += `\n${l.nonprofitName}: ${nonprofitName}`;
      if (nonprofitDirector) body += `\n${l.nonprofitDirector}: ${nonprofitDirector}`;
      if (nonprofitWebsite) body += `\n${l.nonprofitWebsite}: ${nonprofitWebsite}`;
    }
    if (selectedReasons.includes('faithBased') && (faithCity || faithState || faithOrgName || faithWebsite)) {
      body += `\n\n--- ${isEs ? 'Organización Religiosa' : 'Faith-Based Organization'} ---`;
      if (faithOrgName) body += `\n${l.faithOrgName}: ${faithOrgName}`;
      if (faithCity) body += `\n${l.faithCity}: ${faithCity}`;
      if (faithState) body += `\n${l.faithState}: ${faithState}`;
      if (faithWebsite) body += `\n${l.faithWebsite}: ${faithWebsite}`;
    }
    if (selectedReasons.includes('business') && (businessName || businessContact || businessPhone || businessWebsite)) {
      body += `\n\n--- ${isEs ? 'Información del Negocio' : 'Business Details'} ---`;
      if (businessName) body += `\n${l.businessName}: ${businessName}`;
      if (businessContact) body += `\n${l.businessContact}: ${businessContact}`;
      if (businessPhone) body += `\n${l.businessPhone}: ${businessPhone}`;
      if (businessWebsite) body += `\n${l.businessWebsite}: ${businessWebsite}`;
    }
    if (selectedReasons.includes('accomplishments') && (accomplishmentWebsite || accomplishmentSocial || accomplishmentLinkedin)) {
      body += `\n\n--- ${isEs ? 'Información de Logros' : 'Accomplishments Details'} ---`;
      if (accomplishmentWebsite) body += `\n${l.accWebsite}: ${accomplishmentWebsite}`;
      if (accomplishmentSocial) body += `\n${l.accSocial}: ${accomplishmentSocial}`;
      if (accomplishmentLinkedin) body += `\n${l.accLinkedin}: ${accomplishmentLinkedin}`;
    }
    return body;
  };
  const [submitting, setSubmitting] = useState(false);
  const SHEETS_URL = 'https://script.google.com/macros/s/AKfycbyZzuAyC07JGli05_9stgT14MrywttY1WzN1zQTiNnhIfPMxE61PaFIwzqZ2N9mcQSs5Q/exec';
  const sendToGoogleSheets = (data: Record<string, any>) => {
    // Using text/plain content type avoids CORS preflight entirely
    // The JSON string arrives in e.postData.contents on the Apps Script side
    fetch(SHEETS_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'text/plain;charset=utf-8'
      },
      body: JSON.stringify(data)
    }).catch(err => {
      console.warn('Google Sheets submission error:', err);
    });
  };
  const handleSubmit = () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    const submission: NetworkSubmission = {
      id: Date.now().toString() + Math.random().toString(36).slice(2, 8),
      timestamp: new Date().toISOString(),
      firstName,
      lastName,
      email,
      phone,
      city,
      state,
      reasons: selectedReasons,
      socialInstagram: selectedReasons.includes('socialMedia') ? socialInstagram : undefined,
      socialFacebook: selectedReasons.includes('socialMedia') ? socialFacebook : undefined,
      socialTiktok: selectedReasons.includes('socialMedia') ? socialTiktok : undefined,
      nonprofitName: selectedReasons.includes('nonprofit') ? nonprofitName : undefined,
      nonprofitDirector: selectedReasons.includes('nonprofit') ? nonprofitDirector : undefined,
      nonprofitWebsite: selectedReasons.includes('nonprofit') ? nonprofitWebsite : undefined,
      faithCity: selectedReasons.includes('faithBased') ? faithCity : undefined,
      faithState: selectedReasons.includes('faithBased') ? faithState : undefined,
      faithOrgName: selectedReasons.includes('faithBased') ? faithOrgName : undefined,
      faithWebsite: selectedReasons.includes('faithBased') ? faithWebsite : undefined,
      businessName: selectedReasons.includes('business') ? businessName : undefined,
      businessContact: selectedReasons.includes('business') ? businessContact : undefined,
      businessPhone: selectedReasons.includes('business') ? businessPhone : undefined,
      businessWebsite: selectedReasons.includes('business') ? businessWebsite : undefined,
      accomplishmentWebsite: selectedReasons.includes('accomplishments') ? accomplishmentWebsite : undefined,
      accomplishmentSocial: selectedReasons.includes('accomplishments') ? accomplishmentSocial : undefined,
      accomplishmentLinkedin: selectedReasons.includes('accomplishments') ? accomplishmentLinkedin : undefined,
      identifiesHonduran: identifiesHonduran ?? undefined
    };
    // Save locally as backup
    saveSubmission(submission);
    // Send to Google Sheets
    sendToGoogleSheets({
      timestamp: new Date().toISOString(),
      firstName,
      lastName,
      email,
      phone,
      city,
      state,
      identifiesHonduran: identifiesHonduran ?? '',
      reasons: selectedReasons.join(', '),
      socialInstagram: selectedReasons.includes('socialMedia') ? socialInstagram : '',
      socialFacebook: selectedReasons.includes('socialMedia') ? socialFacebook : '',
      socialTiktok: selectedReasons.includes('socialMedia') ? socialTiktok : '',
      nonprofitName: selectedReasons.includes('nonprofit') ? nonprofitName : '',
      nonprofitDirector: selectedReasons.includes('nonprofit') ? nonprofitDirector : '',
      nonprofitWebsite: selectedReasons.includes('nonprofit') ? nonprofitWebsite : '',
      faithCity: selectedReasons.includes('faithBased') ? faithCity : '',
      faithState: selectedReasons.includes('faithBased') ? faithState : '',
      faithOrgName: selectedReasons.includes('faithBased') ? faithOrgName : '',
      faithWebsite: selectedReasons.includes('faithBased') ? faithWebsite : '',
      businessName: selectedReasons.includes('business') ? businessName : '',
      businessContact: selectedReasons.includes('business') ? businessContact : '',
      businessPhone: selectedReasons.includes('business') ? businessPhone : '',
      businessWebsite: selectedReasons.includes('business') ? businessWebsite : '',
      accomplishmentWebsite: selectedReasons.includes('accomplishments') ? accomplishmentWebsite : '',
      accomplishmentSocial: selectedReasons.includes('accomplishments') ? accomplishmentSocial : '',
      accomplishmentLinkedin: selectedReasons.includes('accomplishments') ? accomplishmentLinkedin : ''
    });
    setSubmitting(false);
    setSubmitted(true);
  };
  const handleSendEmail = () => {
    const subject = encodeURIComponent(isEs ? 'Solicitud para Unirse a la Red - HAA' : 'Join Our Network Request - HAA');
    const body = encodeURIComponent(buildMessageBody());
    window.open(`mailto:info@honduranamerican.org?subject=${subject}&body=${body}`, '_blank');
    setSubmitted(true);
  };
  const handleSendWhatsApp = () => {
    const text = encodeURIComponent(`*${isEs ? 'Solicitud para Unirse a la Red - HAA' : 'Join Our Network - HAA'}*\n\n${buildMessageBody()}`);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, '_blank');
    setSubmitted(true);
  };
  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setCity('');
      setState('');
      setSelectedReasons([]);
      setSubmitted(false);
      setSocialInstagram('');
      setSocialFacebook('');
      setSocialTiktok('');
      setNonprofitName('');
      setNonprofitDirector('');
      setNonprofitWebsite('');
      setFaithCity('');
      setFaithState('');
      setFaithOrgName('');
      setFaithWebsite('');
      setBusinessName('');
      setBusinessContact('');
      setBusinessPhone('');
      setBusinessWebsite('');
      setAccomplishmentWebsite('');
      setAccomplishmentSocial('');
      setAccomplishmentLinkedin('');
      setIdentifiesHonduran(null);
    }, 300);
  };
  const isValid = firstName.trim() && lastName.trim();
  return <AnimatePresence data-id="element-2556" data-mp="3om4gj87wl894">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4 pt-20 sm:pt-4" onClick={handleClose} data-id="element-2557" data-mp="3jstavaetuxis">
          <div className="absolute inset-0 bg-haa-navy/60 backdrop-blur-sm" data-id="element-2558" data-mp="2n5vmp09pc5i8" />

          <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 30,
        scale: 0.97
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} onClick={e => e.stopPropagation()} className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto z-10" data-id="element-2559" data-mp="3awhowax0yvkb">
            {/* Header */}
            <div className="sticky top-0 bg-white rounded-t-3xl border-b border-gray-100 px-6 sm:px-8 py-5 flex items-start justify-between z-10" data-id="element-2560" data-mp="1n0jt3y8x7tcb">
              <div data-id="element-2561" data-mp="1wwwmev0k056j">
                <h3 className="font-bebas text-3xl sm:text-4xl text-haa-navy tracking-wider leading-tight" data-id="element-2562" data-mp="1wvbttaxsuwbb">
                  {l.title}
                </h3>
                <p className="font-nunito text-gray-500 text-sm mt-1" data-id="element-2563" data-mp="147pp1zcu5lmv">
                  {l.subtitle}
                </p>
              </div>
              <button onClick={handleClose} className="flex-shrink-0 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors ml-4 mt-1" aria-label={l.close} data-id="element-2564" data-mp="za7btwrsck92">
                <XIcon className="w-4 h-4 text-gray-500" data-id="element-2565" data-mp="lgy6c76ykivt" />
              </button>
            </div>

            <AnimatePresence mode="wait" data-id="element-2566" data-mp="giwr5xcnl3pm">
              {!submitted ? <motion.div key="form" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="px-6 sm:px-8 py-6 space-y-4" data-id="element-2567" data-mp="393mcypks09sj">
                  {/* Name Row */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-2568" data-mp="3rtub0t9mca1v">
                    <div data-id="element-2569" data-mp="3rs9i182ycz64">
                      <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block" data-id="element-2570" data-mp="3mv19lz7lw9i2">
                        {l.firstName} *
                      </label>
                      <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className={inputClass} placeholder={l.firstName} data-id="element-2571" data-mp="5kjn1rzvlcb5" />
                    </div>
                    <div data-id="element-2572" data-mp="hwlq9e2bayxi">
                      <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block" data-id="element-2573" data-mp="hygbyuleqv2n">
                        {l.lastName} *
                      </label>
                      <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className={inputClass} placeholder={l.lastName} data-id="element-2574" data-mp="86an3tqgctro" />
                    </div>
                  </div>

                  {/* Email */}
                  <div data-id="element-2575" data-mp="3lednb6vb292k">
                    <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block" data-id="element-2576" data-mp="3levq6mch3ljg">
                      {l.email}
                    </label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} placeholder="you@example.com" data-id="element-2577" data-mp="1jm7m6as08u2m" />
                  </div>

                  {/* Phone */}
                  <div data-id="element-2578" data-mp="1i40krv5saomx">
                    <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block" data-id="element-2579" data-mp="1i7of2wj1zf2p">
                      {l.phone}
                    </label>
                    <input type="tel" value={phone} onChange={e => setPhone(formatPhoneNumber(e.target.value))} className={inputClass} placeholder="555-123-4567" data-id="element-2580" data-mp="188yyr86t22ok" />
                  </div>

                  {/* City + State */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-2581" data-mp="1r3xeib716dou">
                    <div data-id="element-2582" data-mp="2omfjvbbark20">
                      <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block" data-id="element-2583" data-mp="2omp3mh5r0uz9">
                        {l.city}
                      </label>
                      <input type="text" value={city} onChange={e => setCity(e.target.value)} className={inputClass} placeholder={l.city} data-id="element-2584" data-mp="2s4w9hhdb1vgj" />
                    </div>
                    <div className="relative" data-id="element-2585" data-mp="364e3cb5gjywr">
                      <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block" data-id="element-2586" data-mp="3b2pcskruc7eg">
                        {l.state}
                      </label>
                      <button type="button" onClick={() => setStateDropdownOpen(!stateDropdownOpen)} className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 font-nunito text-sm text-left flex items-center justify-between focus:border-haa-blue outline-none transition-colors" data-id="element-2587" data-mp="3jurawceftblk">
                        <span className={state ? 'text-haa-navy' : 'text-gray-400'} data-id="element-2588" data-mp="2jzxkfdu837w">
                          {state || l.selectState}
                        </span>
                        <ChevronDownIcon className="w-4 h-4 text-gray-400" data-id="element-2589" data-mp="193ava1aekebb" />
                      </button>
                      <AnimatePresence data-id="element-2590" data-mp="144ppzngan4g0">
                        {stateDropdownOpen && <motion.div initial={{
                    opacity: 0,
                    y: -4
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -4
                  }} transition={{
                    duration: 0.15
                  }} className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-20 max-h-48 overflow-y-auto" data-id="element-2591" data-mp="gen4jq6vwwqd">
                            {US_STATES.map(s => <button key={s} type="button" onClick={() => {
                      setState(s);
                      setStateDropdownOpen(false);
                    }} className={`w-full text-left px-4 py-2 font-nunito text-sm hover:bg-blue-50 transition-colors ${state === s ? 'bg-blue-50 text-haa-blue font-bold' : 'text-gray-700'}`} data-id="element-2592" data-mp="1ufrubgnfiyff">
                                {s}
                              </button>)}
                          </motion.div>}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* Honduran Identity Question */}
                  <div data-id="element-2593" data-mp="3fpvl0d752gcf">
                    <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block" data-id="element-2594" data-mp="3fqou3ktklhxq">
                      {isEs ? '¿Te identificas como hondureño-americano/hondureño?' : 'Do you identify as Honduran-American/Honduran?'}
                    </label>
                    <div className="flex gap-3" data-id="element-2595" data-mp="31r5lys0re6lf">
                      <button type="button" onClick={() => setIdentifiesHonduran('yes')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 font-nunito text-sm font-semibold transition-all duration-200 ${identifiesHonduran === 'yes' ? 'border-haa-blue bg-blue-50 text-haa-navy' : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-2596" data-mp="2wym7lsu8z7fk">
                        {identifiesHonduran === 'yes' && <CheckIcon className="w-4 h-4 text-haa-blue" data-id="element-2597" data-mp="thzi0m986llz" />}
                        {isEs ? 'Sí' : 'Yes'}
                      </button>
                      <button type="button" onClick={() => setIdentifiesHonduran('no')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 font-nunito text-sm font-semibold transition-all duration-200 ${identifiesHonduran === 'no' ? 'border-haa-blue bg-blue-50 text-haa-navy' : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-2598" data-mp="13hghun1sq5a1">
                        {identifiesHonduran === 'no' && <CheckIcon className="w-4 h-4 text-haa-blue" data-id="element-2599" data-mp="okzcccgafkve" />}
                        No
                      </button>
                    </div>
                  </div>

                  {/* Reason for Contacting */}
                  <div data-id="element-2600" data-mp="2llyn3xsgpozo">
                    <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block" data-id="element-2601" data-mp="2ll5l1a4g7nnv">
                      {l.reasonLabel}
                    </label>
                    <p className="font-nunito text-[11px] text-gray-400 italic mb-2" data-id="element-2602" data-mp="2vko4zqkcz9fx">
                      {isEs ? 'Seleccione todas las que apliquen' : 'Check all that apply'}
                    </p>
                    <div className="space-y-2" data-id="element-2603" data-mp="2zky7uucutjqk">
                      {REASON_OPTIONS.map(opt => {
                  const isSelected = selectedReasons.includes(opt.key);
                  return <div key={opt.key} data-id="element-2604" data-mp="39a814djfp8g9">
                            <button type="button" onClick={() => toggleReason(opt.key)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left font-nunito text-sm transition-all duration-200 ${isSelected ? 'border-haa-blue bg-blue-50 text-haa-navy' : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-2605" data-mp="39b9mbau5zbbp">
                              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200 ${isSelected ? 'border-haa-blue bg-haa-blue' : 'border-gray-300'}`} data-id="element-2606" data-mp="3svhyw99dnv8g">
                                {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-2607" data-mp="3nz9n95ue2k61" />}
                              </div>
                              <span className="leading-snug" data-id="element-2608" data-mp="3e6lwosu05cfr">
                                {getReasonLabel(opt)}
                              </span>
                            </button>

                            {/* Conditional sub-fields for Nonprofit */}
                            <AnimatePresence data-id="element-2609" data-mp="358jq7xri1rl3">
                              {opt.key === 'nonprofit' && isSelected && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.25,
                        ease: [0.16, 1, 0.3, 1]
                      }} className="overflow-hidden" data-id="element-2610" data-mp="37hcr4xqqwwr4">
                                  <div className="ml-8 mt-2 mb-1 space-y-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100" data-id="element-2611" data-mp="czhlvfffv1lc">
                                    <div data-id="element-2612" data-mp="i64xvmf380s1">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2613" data-mp="i5d1489r7x06">
                                        {l.nonprofitName}
                                      </label>
                                      <input type="text" value={nonprofitName} onChange={e => setNonprofitName(e.target.value)} className={subInputClass} placeholder={l.nonprofitName} data-id="element-2614" data-mp="15vz6tswad570" />
                                    </div>
                                    <div data-id="element-2615" data-mp="1jugqnfj2lxjn">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2616" data-mp="3e45ia5ehvxqm">
                                        {l.nonprofitDirector}
                                      </label>
                                      <input type="text" value={nonprofitDirector} onChange={e => setNonprofitDirector(e.target.value)} className={subInputClass} placeholder={l.nonprofitDirector} data-id="element-2617" data-mp="34dj6yndt9tdq" />
                                    </div>
                                    <div data-id="element-2618" data-mp="jnbsd3r8lb8d">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2619" data-mp="jomsmtlh40uq">
                                        {l.nonprofitWebsite}
                                      </label>
                                      <input type="url" value={nonprofitWebsite} onChange={e => setNonprofitWebsite(e.target.value)} className={subInputClass} placeholder="https://" data-id="element-2620" data-mp="246e561mrqoxp" />
                                    </div>
                                  </div>
                                </motion.div>}
                            </AnimatePresence>

                            {/* Conditional sub-fields for Faith-Based Organization */}
                            <AnimatePresence data-id="element-2621" data-mp="12gsat9cq51up">
                              {opt.key === 'faithBased' && isSelected && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.25,
                        ease: [0.16, 1, 0.3, 1]
                      }} className="overflow-hidden" data-id="element-2622" data-mp="13wnwudkhggdl">
                                  <div className="ml-8 mt-2 mb-1 space-y-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100" data-id="element-2623" data-mp="7fjjtlumzxfo">
                                    <div data-id="element-2624" data-mp="2kp1hoc64lz3o">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2625" data-mp="2knqeadgletd3">
                                        {l.faithOrgName}
                                      </label>
                                      <input type="text" value={faithOrgName} onChange={e => setFaithOrgName(e.target.value)} className={subInputClass} placeholder={l.faithOrgName} data-id="element-2626" data-mp="26r33b6rt22o6" />
                                    </div>
                                    <div data-id="element-2627" data-mp="1j2tlsh1mk12u">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2628" data-mp="1e14dva9p83iz">
                                        {l.faithCity}
                                      </label>
                                      <input type="text" value={faithCity} onChange={e => setFaithCity(e.target.value)} className={subInputClass} placeholder={l.faithCity} data-id="element-2629" data-mp="19eeld0w7x8nc" />
                                    </div>
                                    <div data-id="element-2630" data-mp="1idjka8boz401">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2631" data-mp="1icgszzagizjl">
                                        {l.faithState}
                                      </label>
                                      <input type="text" value={faithState} onChange={e => setFaithState(e.target.value)} className={subInputClass} placeholder={l.faithState} data-id="element-2632" data-mp="2avx619jr6zir" />
                                    </div>
                                    <div data-id="element-2633" data-mp="37nlvkfvzr7tz">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2634" data-mp="37on8yr0hc6ev">
                                        {l.faithWebsite}
                                      </label>
                                      <input type="url" value={faithWebsite} onChange={e => setFaithWebsite(e.target.value)} className={subInputClass} placeholder="https://" data-id="element-2635" data-mp="11ssxj4h5xhpz" />
                                    </div>
                                  </div>
                                </motion.div>}
                            </AnimatePresence>

                            {/* Conditional sub-fields for Social Media */}
                            <AnimatePresence data-id="element-2636" data-mp="3j1vufo24cnqa">
                              {opt.key === 'socialMedia' && isSelected && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.25,
                        ease: [0.16, 1, 0.3, 1]
                      }} className="overflow-hidden" data-id="element-2637" data-mp="3o18ksis78cxm">
                                  <div className="ml-8 mt-2 mb-1 space-y-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100" data-id="element-2638" data-mp="1v44k79x5twzq">
                                    <div data-id="element-2639" data-mp="1q5tenbdwe8nb">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2640" data-mp="1q74fp3fyl2aq">
                                        {l.instagram}
                                      </label>
                                      <input type="text" value={socialInstagram} onChange={e => setSocialInstagram(e.target.value)} className={subInputClass} placeholder="@username" data-id="element-2641" data-mp="6nwekejjkl14" />
                                    </div>
                                    <div data-id="element-2642" data-mp="3f1vchlq2qgh9">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2643" data-mp="3a3tpippj1x0i">
                                        {l.facebook}
                                      </label>
                                      <input type="text" value={socialFacebook} onChange={e => setSocialFacebook(e.target.value)} className={subInputClass} placeholder="facebook.com/yourpage" data-id="element-2644" data-mp="312vma9mwxtiz" />
                                    </div>
                                    <div data-id="element-2645" data-mp="sw1s3tqzhcli">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2646" data-mp="sxcym1btcygb">
                                        {l.tiktok}
                                      </label>
                                      <input type="text" value={socialTiktok} onChange={e => setSocialTiktok(e.target.value)} className={subInputClass} placeholder="@username" data-id="element-2647" data-mp="16n6magbl1cog" />
                                    </div>
                                  </div>
                                </motion.div>}
                            </AnimatePresence>

                            {/* Conditional sub-fields for Business */}
                            <AnimatePresence data-id="element-2648" data-mp="yj766yxqod9o">
                              {opt.key === 'business' && isSelected && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.25,
                        ease: [0.16, 1, 0.3, 1]
                      }} className="overflow-hidden" data-id="element-2649" data-mp="17jlw6blnegji">
                                  <div className="ml-8 mt-2 mb-1 space-y-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100" data-id="element-2650" data-mp="357zgk4fk15ld">
                                    <div data-id="element-2651" data-mp="2vhe8zrhwkqqb">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2652" data-mp="2vgcvlgdezs5f">
                                        {l.businessName}
                                      </label>
                                      <input type="text" value={businessName} onChange={e => setBusinessName(e.target.value)} className={subInputClass} placeholder={l.businessName} data-id="element-2653" data-mp="3o48qcfjfb9zk" />
                                    </div>
                                    <div data-id="element-2654" data-mp="2nxwv82o20b80">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2655" data-mp="2o115nhs44374">
                                        {l.businessContact}
                                      </label>
                                      <input type="text" value={businessContact} onChange={e => setBusinessContact(e.target.value)} className={subInputClass} placeholder={l.businessContact} data-id="element-2656" data-mp="29atyvzbgw8m9" />
                                    </div>
                                    <div data-id="element-2657" data-mp="3jaelrr93shre">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2658" data-mp="3o7epvwme7afv">
                                        {l.businessPhone}
                                      </label>
                                      <input type="tel" value={businessPhone} onChange={e => setBusinessPhone(formatPhoneNumber(e.target.value))} className={subInputClass} placeholder="555-123-4567" data-id="element-2659" data-mp="10cccnltxbbn" />
                                    </div>
                                    <div data-id="element-2660" data-mp="2pm4o3gg432lp">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2661" data-mp="2pnz5wlw36elk">
                                        {l.businessWebsite}
                                      </label>
                                      <input type="text" value={businessWebsite} onChange={e => setBusinessWebsite(e.target.value)} className={subInputClass} placeholder="https:// or @handle" data-id="element-2662" data-mp="2ulqyli4k24qt" />
                                    </div>
                                  </div>
                                </motion.div>}
                            </AnimatePresence>

                            {/* Conditional sub-fields for Accomplishments */}
                            <AnimatePresence data-id="element-2663" data-mp="v9nta5em8qz0">
                              {opt.key === 'accomplishments' && isSelected && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.25,
                        ease: [0.16, 1, 0.3, 1]
                      }} className="overflow-hidden" data-id="element-2664" data-mp="gbn1nnkrt1ao">
                                  <div className="ml-8 mt-2 mb-1 space-y-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100" data-id="element-2665" data-mp="31jg971qaq80q">
                                    <div data-id="element-2666" data-mp="2wl4xelgfxnfv">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2667" data-mp="2rkh0jj5j6cdq">
                                        {l.accWebsite}
                                      </label>
                                      <input type="url" value={accomplishmentWebsite} onChange={e => setAccomplishmentWebsite(e.target.value)} className={subInputClass} placeholder="https://" data-id="element-2668" data-mp="2il3xp9bwwm1z" />
                                    </div>
                                    <div data-id="element-2669" data-mp="1fr6ieyceansg">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2670" data-mp="1fs85602rwyq0">
                                        {l.accSocial}
                                      </label>
                                      <input type="text" value={accomplishmentSocial} onChange={e => setAccomplishmentSocial(e.target.value)} className={subInputClass} placeholder="@handle" data-id="element-2671" data-mp="1otzcf9cypdlz" />
                                    </div>
                                    <div data-id="element-2672" data-mp="3rgfq3n145u2d">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2673" data-mp="3rfd1xmv6e5q5">
                                        {l.accLinkedin}
                                      </label>
                                      <input type="url" value={accomplishmentLinkedin} onChange={e => setAccomplishmentLinkedin(e.target.value)} className={subInputClass} placeholder="https://linkedin.com/in/..." data-id="element-2674" data-mp="33js3zhom1tkc" />
                                    </div>
                                  </div>
                                </motion.div>}
                            </AnimatePresence>
                          </div>;
                })}
                    </div>
                  </div>

                  {/* Required note */}
                  <p className="font-nunito text-xs text-gray-400 pt-1" data-id="element-2675" data-mp="3jxevxsc7dcro">
                    {l.required}
                  </p>

                  {/* Submit Buttons */}
                  <div className="flex flex-col gap-3 pt-2 pb-2" data-id="element-2676" data-mp="3ewitlj7i1yod">
                    <motion.button whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={handleSubmit} disabled={!isValid} className={`w-full flex items-center justify-center gap-2 font-nunito font-bold text-base px-6 py-4 rounded-full shadow-lg transition-all ${isValid ? 'bg-gradient-to-r from-haa-blue to-haa-midblue text-white hover:shadow-xl' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-2677" data-mp="1pkgm19uf0i4v">
                      <SendIcon className="w-4 h-4" data-id="element-2678" data-mp="2t5rcolr4jnkr" />
                      {isEs ? 'Enviar' : 'Submit'}
                    </motion.button>
                  </div>
                </motion.div> : <motion.div key="success" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="px-6 sm:px-8 py-16 flex flex-col items-center text-center" data-id="element-2679" data-mp="36yz3wcbamh1x">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.1,
              type: 'spring',
              stiffness: 200
            }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-5" data-id="element-2680" data-mp="10aq4g3kpncur">
                    <CheckIcon className="w-8 h-8 text-green-600" data-id="element-2681" data-mp="1eahq8qhgxxjb" />
                  </motion.div>
                  <h3 className="font-bebas text-4xl text-haa-navy tracking-wider mb-2" data-id="element-2682" data-mp="1e8521vl4aqxt">
                    {l.successTitle}
                  </h3>
                  <p className="font-nunito text-gray-600 text-base mb-8 max-w-sm" data-id="element-2683" data-mp="26rtzrjws30iq">
                    {l.successMessage}
                  </p>
                  <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={handleClose} className="inline-flex items-center gap-2 bg-haa-navy text-white font-nunito font-bold text-base px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all" data-id="element-2684" data-mp="21y8ztmbmjwgw">
                    {l.close}
                  </motion.button>
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}